import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Trash2, RefreshCw, ShieldCheck, Clock, Star, PlusCircle, BookOpen, Users } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { useTranslate } from "../context/TranslateContext";
import { LANGUAGES } from "../mock/data";
import { listContent, moderateContent, deleteContent } from "../lib/api";

const FILTERS = ["all", "pending", "approved", "rejected"];
const langName = (code) => LANGUAGES.find((l) => l.code === code) || { code, name: code, native: code };
const STATUS_STYLE = {
  pending: "border-saffron/40 bg-saffron/10 text-saffron",
  approved: "border-emerald2/40 bg-emerald2/10 text-emerald2",
  rejected: "border-crimson/40 bg-crimson/10 text-crimson",
};

export default function AdminContent() {
  const { t } = useTranslate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listContent(filter === "all" ? undefined : filter);
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load submissions"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const doModerate = async (id, action) => {
    try { await moderateContent(id, action); toast.success(t(action === "approve" ? "Approved" : "Rejected")); load(); }
    catch (e) { toast.error("Action failed"); }
  };
  const doDelete = async (id) => {
    if (!window.confirm(t("Delete this submission permanently?"))) return;
    try { await deleteContent(id); toast.success(t("Deleted")); setItems((s) => s.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Delete failed"); }
  };

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "all" ? items.length : items.filter((x) => x.status === f).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-content-page">
      <InnerNav />
      <div className="mx-auto max-w-5xl px-6 py-14 md:py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <ShieldCheck className="h-4 w-4" /> {t("Editor Dashboard")}
            </div>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-cream sm:text-5xl">
              {t("Content moderation")}
            </h1>
            <p className="mt-3 font-body text-sm text-ash">{t("Review, translate-check and publish community and editorial submissions. (Demo — no role gating.)")}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="refresh-btn" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-body text-sm text-ash hover:border-gold hover:text-gold">
              <RefreshCw className="h-4 w-4" /> {t("Refresh")}
            </button>
            <Link to="/admin/users" data-testid="go-users" className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-body text-sm text-gold hover:bg-gold/10">
              <Users className="h-4 w-4" /> {t("Users")}
            </Link>
            <Link to="/library" data-testid="view-library" className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-body text-sm text-gold hover:bg-gold/10">
              <BookOpen className="h-4 w-4" /> {t("View live Library")}
            </Link>
            <Link to="/contribute" data-testid="new-submission" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 font-body text-sm font-medium text-ink hover:scale-105 transition-transform">
              <PlusCircle className="h-4 w-4" /> {t("New submission")}
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="status-filters">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-2 font-body text-sm capitalize transition-colors ${
                filter === f ? "border-gold bg-gold/10 text-gold" : "border-edge text-ash hover:border-gold hover:text-gold"
              }`}>
              {t(f)}{filter === f ? ` · ${counts[f]}` : ""}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-5">
          {loading && <div className="py-16 text-center font-body text-ash" data-testid="admin-loading">{t("Loading…")}</div>}
          {!loading && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-edge bg-surface/30 py-16 text-center font-body text-ash" data-testid="admin-empty">
              {t("No submissions yet.")} <Link to="/contribute" className="text-gold hover:underline">{t("Create the first one")}</Link>.
            </div>
          )}
          {!loading && items.map((it) => (
            <SubmissionCard key={it.id} it={it} onModerate={doModerate} onDelete={doDelete} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}

const SubmissionCard = ({ it, onModerate, onDelete, t }) => {
  const codes = Object.keys(it.langs || {});
  const [active, setActive] = useState(it.primary_lang && it.langs[it.primary_lang] ? it.primary_lang : codes[0]);
  const v = (it.langs && it.langs[active]) || { title: "", summary: "", body: "" };
  const rtl = !!langName(active).rtl;

  return (
    <div className="rounded-2xl border border-edge bg-surface/40 p-6" data-testid={`submission-${it.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-edge bg-surface px-2.5 py-0.5 font-body text-xs uppercase tracking-wider text-ash">{t(it.type)}</span>
            <span className={`rounded-full border px-2.5 py-0.5 font-body text-xs capitalize ${STATUS_STYLE[it.status] || ""}`} data-testid={`status-${it.id}`}>{t(it.status)}</span>
            <span className="flex items-center gap-1 font-body text-xs text-ash"><Clock className="h-3 w-3" />{(it.created_at || "").slice(0, 10)}</span>
          </div>
          <h3 className="mt-3 truncate font-display text-2xl text-cream">{it.langs?.[it.primary_lang]?.title || t("Untitled")}</h3>
          <div className="mt-1 font-body text-sm text-ash">{t("by")} {it.author_name} · {it.author_email}</div>
          {it.tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {it.tags.map((tg) => <span key={tg} className="rounded-full bg-black/[0.05] px-2 py-0.5 font-body text-xs text-ash">#{tg}</span>)}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {it.status !== "approved" && (
            <button data-testid={`approve-${it.id}`} onClick={() => onModerate(it.id, "approve")}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald2/50 bg-emerald2/10 px-4 py-2 font-body text-sm text-emerald2 hover:bg-emerald2/20">
              <Check className="h-4 w-4" /> {t("Approve")}
            </button>
          )}
          {it.status !== "rejected" && (
            <button data-testid={`reject-${it.id}`} onClick={() => onModerate(it.id, "reject")}
              className="inline-flex items-center gap-1.5 rounded-full border border-crimson/50 bg-crimson/10 px-4 py-2 font-body text-sm text-crimson hover:bg-crimson/20">
              <X className="h-4 w-4" /> {t("Reject")}
            </button>
          )}
          <button data-testid={`delete-${it.id}`} onClick={() => onDelete(it.id)}
            className="inline-flex items-center justify-center rounded-full border border-edge p-2 text-ash hover:border-crimson hover:text-crimson">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Language versions */}
      <div className="mt-5 flex flex-wrap gap-2">
        {codes.map((c) => (
          <button key={c} data-testid={`view-lang-${it.id}-${c}`} onClick={() => setActive(c)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-body text-xs transition-colors ${
              active === c ? "border-gold bg-gold/10 text-gold" : "border-edge text-ash hover:border-gold hover:text-gold"
            }`}>
            {c === it.primary_lang && <Star className="h-3 w-3 fill-gold text-gold" />}
            {langName(c).native} <span className="opacity-70">{langName(c).name}</span>
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-edge bg-paper/60 p-5" dir={rtl ? "rtl" : "ltr"} data-testid={`preview-${it.id}`}>
        <div className="font-display text-xl text-cream">{v.title || <span className="text-ash">{t("(no title in this language)")}</span>}</div>
        {v.summary && <div className="mt-2 font-body text-sm italic text-ash">{v.summary}</div>}
        {v.body && <p className="mt-3 whitespace-pre-wrap font-body text-base leading-relaxed text-cream">{v.body}</p>}
      </div>
    </div>
  );
};
